import { useCallback, useEffect } from "react";
import { useBlocker } from "react-router";

/**
 * Blocks in-app navigation while work pattern import progress is uncommitted.
 * Shows a confirmation modal via {@link WorkPatternImportLeaveConfirmModal}
 * when the user attempts to leave (#400).
 */
export function useWorkPatternImportNavigationGuard(shouldBlock: boolean) {
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (!shouldBlock) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [shouldBlock]);

  const stay = useCallback(() => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  }, [blocker]);

  const leaveAnyway = useCallback(() => {
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
  }, [blocker]);

  return {
    isBlocked: blocker.state === "blocked",
    stay,
    leaveAnyway,
  };
}
